import { Check, Copy, FileText, Link as LinkIcon, Loader2, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useLazyGetSavedTourShareTextQuery,
  useLazyGetSavedToursSessionShareTextQuery,
} from "../../../../../app/services/book/saved_tours"
import { useGetTourShareTemplatesQuery } from "../../../../../app/services/book/tour_share_templates"

const isHtmlString = (str) => /<[a-z][\s\S]*>/i.test(str || "")

const htmlToPlainText = (html) => {
  const div = document.createElement("div")
  div.innerHTML = html
  return div.textContent || div.innerText || ""
}

const ShareModal = ({ target, link, open, onClose }) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState("template") // "template" | "link"
  const [templateId, setTemplateId] = useState(null)
  const [copied, setCopied] = useState(false)

  const isSession = target?.type === "session"

  const { data: templatesData } = useGetTourShareTemplatesQuery(undefined, { skip: !open })
  const templates = templatesData?.templates || []

  const [fetchTourShareText, tourShareState] = useLazyGetSavedTourShareTextQuery()
  const [fetchSessionShareText, sessionShareState] = useLazyGetSavedToursSessionShareTextQuery()
  const { data: shareData, isFetching } = isSession ? sessionShareState : tourShareState

  useEffect(() => {
    if (!open || tab !== "template" || !target) return
    if (isSession) {
      fetchSessionShareText({
        session_key: target.sessionKey,
        template_id: templateId || undefined,
      })
    } else {
      fetchTourShareText({ id: target.id, template_id: templateId || undefined })
    }
  }, [open, tab, target, isSession, templateId, fetchSessionShareText, fetchTourShareText])

  useEffect(() => {
    if (!open) return
    if (templateId == null && templates.length) {
      const def = templates.find((tpl) => tpl.is_default) || templates[0]
      setTemplateId(def.id)
    }
  }, [open, templates, templateId])

  useEffect(() => {
    if (!open) {
      setTemplateId(null)
      setTab("template")
    }
  }, [open])

  const text = shareData?.text || ""
  const isHtml = tab === "template" && isHtmlString(text)
  const copyValue = tab === "link" ? link || "" : text

  const handleCopy = async () => {
    if (!copyValue) return
    try {
      if (isHtml && "ClipboardItem" in window) {
        const plain = htmlToPlainText(copyValue)
        const item = new ClipboardItem({
          "text/plain": new Blob([plain], { type: "text/plain" }),
          "text/html": new Blob([copyValue], { type: "text/html" }),
        })
        await navigator.clipboard.write([item])
      } else if (isHtml) {
        await navigator.clipboard.writeText(htmlToPlainText(copyValue))
      } else {
        await navigator.clipboard.writeText(copyValue)
      }
    } catch (err) {
      await navigator.clipboard.writeText(isHtml ? htmlToPlainText(copyValue) : copyValue)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E7EAEF] shadow-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[#1F2937]">
            {isSession ? t("saved_tours.share.title_session") : t("saved_tours.share.title")}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#9AA5B4] hover:bg-[#F4F6F9] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center gap-1 mb-3 p-1 bg-[#F4F6F9] rounded-xl">
          <button
            onClick={() => setTab("template")}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg py-1.5 transition-colors ${
              tab === "template" ? "bg-white text-[#1F2937] shadow-sm" : "text-[#7C8697]"
            }`}
          >
            <FileText size={13} /> {t("saved_tours.share.tab_template")}
          </button>
          <button
            onClick={() => setTab("link")}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg py-1.5 transition-colors ${
              tab === "link" ? "bg-white text-[#1F2937] shadow-sm" : "text-[#7C8697]"
            }`}
          >
            <LinkIcon size={13} /> {t("saved_tours.share.tab_link")}
          </button>
        </div>

        {tab === "template" && (
          <>
            <div className="mb-3">
              <select
                value={templateId || ""}
                onChange={(e) => setTemplateId(Number(e.target.value))}
                className="w-full text-sm border border-[#E7EAEF] rounded-xl px-3 py-2
                           text-[#1F2937] bg-white focus:outline-none focus:ring-2 focus:ring-[#1F2937]/10"
              >
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                    {tpl.is_default ? ` (${t("saved_tours.share.default")})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative">
              {isFetching ? (
                <div className="h-[132px] flex items-center justify-center border border-[#E7EAEF] rounded-xl bg-[#F9FAFB] text-[#9AA5B4]">
                  <Loader2 size={18} className="animate-spin" />
                </div>
              ) : isHtml ? (
                <div
                  className="w-full text-sm border border-[#E7EAEF] rounded-xl p-3 bg-[#F9FAFB]
                             text-[#1F2937] overflow-y-auto h-[132px]
                             [&_ol]:list-decimal [&_ul]:list-disc [&_ol]:pl-5 [&_ul]:pl-5
                             [&_p]:mb-1 [&_li]:mb-0.5"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              ) : (
                <textarea
                  readOnly
                  value={text}
                  rows={6}
                  className="w-full text-sm border border-[#E7EAEF] rounded-xl p-3 bg-[#F9FAFB]
                             text-[#1F2937] resize-none focus:outline-none"
                />
              )}
            </div>
          </>
        )}

        {tab === "link" && (
          <textarea
            readOnly
            value={link || ""}
            rows={3}
            className="w-full text-sm border border-[#E7EAEF] rounded-xl p-3 bg-[#F9FAFB]
                       text-[#1F2937] resize-none focus:outline-none break-all"
          />
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleCopy}
            disabled={!copyValue || (tab === "template" && isFetching)}
            className="flex-1 flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl
                       border border-[#E7EAEF] text-sm font-medium text-[#1F2937]
                       hover:bg-[#F4F6F9] transition-colors disabled:opacity-50"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? t("saved_tours.share.copied") : t("saved_tours.share.copy")}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShareModal
